<template>
  <div class="description">
    <div class="description-top">
      <div class="top-left">
        <div
          v-if="soldCount > 1000 && [2, 5, 8].includes(soldCount % 10)"
          class="bestseller"
        >
          Bestseller
        </div>
        <div class="content">
          {{ detailInfo?.subjectTrans }}
          <span
            v-if="detailInfo?.report"
            class="report-icon"
          >
            <Tooltip
              :offset="4"
              content="There are user reports, please choose carefully."
            >
              <Icon name="icon-warning-outline" />
            </Tooltip>
          </span>
          <span
            v-else
            @click="openReportDialog"
            class="report"
          >Report</span>
        </div>
      </div>
      <div
        class="top-right"
      >
        <Icon
          class="icon"
          :name="isFavorite ? 'favorite-full' : 'favorite-outline'"
          @click="handleCollect"
        />
      </div>
    </div>
    <div
      class="best-supplier"
      v-if="factory?.honestMerchant && factory.honestMerchant >= 5"
    >
      <img
        src="@/assets/colorIcon/best-supplier.svg"
        alt=""
      >
      <span class="dot">·</span>
      <img
        class="img2"
        src="@/assets/colorIcon/best-manufacturer.svg"
        alt=""
      >
      <span class="dot">·</span>
      <span class="years">{{ factory.honestMerchant }} years</span>
    </div>
    <div
      v-if="detailInfo?.sellerDataInfo"
      class="product-rating"
    >
      <template v-if="Number(detailInfo.sellerDataInfo.compositeServiceScore) > 0">
        <div class="star-rating">
          <StarRate :value="detailInfo.sellerDataInfo.compositeServiceScore" />
          <div class="rating-num">
            {{ Number(detailInfo.sellerDataInfo.compositeServiceScore).toFixed(1) }}
          </div>
        </div>
      </template>
      <template v-if="soldCount > 0">
        <div
          v-if="Number(detailInfo.sellerDataInfo.compositeServiceScore) > 0"
          class="separator"
        />
        <div class="sold-count">
          <span>{{ formatSalesVolume(soldCount) }}</span> sold
          <Tooltip
            :offset="4"
            content="Global sales of the product over the past year."
          >
            <Icon name="icon-help-tip-l" />
          </Tooltip>
        </div>
      </template>
      <template v-if="showAdding">
        <div class="separator" />
        <div class="adding-cart">
          {{ addingCount }} adding to cart
        </div>
      </template>
      <template v-if="!showAdding && Number(detailInfo.sellerDataInfo.repeatPurchasePercent) > 0">
        <div class="separator" />
        <div class="custom-rate">
          Customer reorder rate <span>{{ (detailInfo.sellerDataInfo.repeatPurchasePercent * 100).toFixed(0) }}</span>%
        </div>
      </template>
    </div>
  </div>

  <ReportDialog
    v-if="reportDialogVisible"
    @close="reportDialogVisible = false"
    type="PRODUCT"
    :target-id="$route.params.id"
  />
</template>

<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { formatSalesVolume, buriedPointUtil } from '@/utils/utils'
import { usePlugin } from '@/hooks/usePlugin'
import Icon from '@/components/Icon/Icon.vue'
import { StarRate } from '@/components/StarRate'
import { addFavorite, removeFavorite } from '@/api/favorite'
import { Tooltip } from '@/components/Tooltip'
import { useUserStore } from '@/pinia/user'
import { useLoginModal } from '@/components/Login'
import { ReportDialog } from '@/components/ReportDialog'
import { usePcSideCart } from '@/components/PCSideCart/usePcSideCart'
import { useFactory } from '@/components/SupplierHeader/useFactory'

const props = defineProps({
  detailInfo: {
    type: Object,
    default: () => ({})
  }
})

const { factory } = useFactory(computed(() => props.detailInfo?.sellerOpenId))

const { showPCSideCart, hidePCSideCart } = usePcSideCart()

const route = useRoute()

const { $toast } = usePlugin()

const user = useUserStore()

const { open: openLoginModal } = useLoginModal()

const isFavorite = ref(props.detailInfo?.collect)

const reportDialogVisible = ref(false)

const soldCount = computed(() => {
  if (route.query.key) {
    const queryCount = atob(route.query.key)
    if (props.detailInfo?.soldOut > queryCount) {
      return props.detailInfo?.soldOut
    }
    return queryCount
  } else {
    return props.detailInfo?.soldOut
  }
})

const showAdding = computed(() => {
  return route.query.adding &&
    soldCount.value > 300 &&
    props.detailInfo.sellerDataInfo.repeatPurchasePercent > 0.6 &&
    [2, 3, 6, 7].includes(soldCount.value % 10)
})

const addingCount = computed(() => {
  return atob(route.query.adding)
})

watch(reportDialogVisible, (value) => {
  if (value) {
    hidePCSideCart()
  } else {
    showPCSideCart()
  }
})

const onFavorite = async () => {
  const { code } = await addFavorite({
    type: 'PRODUCT',
    userCollectSaveProductVo: {
      offerId: props.detailInfo.offerId
    }
  })
  if (code === 200) {
    isFavorite.value = true
    $toast('Followed product successfully.')
  }
}

const onUnFavorite = async () => {
  const { code } = await removeFavorite({
    type: 'PRODUCT',
    targetId: props.detailInfo.offerId
  })
  if (code === 200) {
    isFavorite.value = false
  }
}

const handleCollect = async () => {
  if (!user.isLogged) {
    openLoginModal()
    return
  }

  buriedPointUtil({
    eventId: 'PPD208',
    others: {
      type: isFavorite.value ? 'Followed' : 'Follow',
      followKey: route.params.id
    }
  })

  if (isFavorite.value) {
    return onUnFavorite()
  } else {
    return onFavorite()
  }
}

const openReportDialog = () => {
  buriedPointUtil({ eventId: 'PPD206', others: { type: 'Products', objectId: props.detailInfo.offerId } })
  reportDialogVisible.value = true
}
</script>

<style lang='less' scoped>
.description {
  padding-bottom: 24px;
  border-bottom: 1px solid #EBF1F5;
  .description-top {
    display: flex;
    gap: 24px;
    margin-bottom: 10px;
    .top-left {
      .bestseller {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 68px;
        height: 22px;
        border-radius: 4px;
        color: #1B3E9C;
        font-size: 11px;
        font-family: mon-700;
        background-color: #D3E1FD;
        margin-bottom: 4px;
      }
      .content {
        font-family: mon-700;
        font-size: 16px;
        line-height: 20px;
        color: #232323;
        .report-icon {
          color: #DB7E25;
          font-size: 12px;
          cursor: pointer;
        }
        .report {
          vertical-align: middle;
          color: #5c5c5c;
          font-size: 11px;
          font-family: mon-500;
          text-decoration: underline;
          cursor: pointer;
          margin-left: 2px;
          &:hover {
            color: var(--link-hover-color);
          }
        }
      }
    }
    .top-right {
      flex-shrink: 0;
      display: flex;
      justify-content: center;
      font-size: 32px;
      color: #232323;

      > .icon {
        cursor: pointer;
      }
    }
  }
  .best-supplier {
    display: flex;
    align-items: center;
    height: 18px;
    margin-bottom: 6px;
    .img2 {
      position: relative;
      top: -2px;
    }
    .dot {
      color: #B2B7C2;
      margin: 0 4px;
    }
    .years {
      color: #232323;
      font-size: 11px;
      font-family: mon-500;
    }
  }
  .product-rating {
    display: flex;
    align-items: center;
    height: 14px;

    .star-rating {
      display: flex;
      align-items: center;
      gap: 2px;
      .rating-num {
        font-size: 11px;
        font-family: mon-600;
        color: #232323;
      }
    }
    .sold-count {
      display: flex;
      align-items: center;
      color: #232323;
      font-size: 11px;
      font-family: mon-500;
      .tooltip-wrapper {
        padding-left: 2px;
        padding-top: 0.5px;
      }
      span {
        font-family: mon-600;
      }
    }
    .adding-cart {
      color: #1153D6;
      font-size: 11px;
      font-family: mon-500;
    }
    .custom-rate {
      color: #232323;
      font-size: 11px;
      font-family: mon-500;
      span {
        font-family: mon-600;
      }
    }
    .separator {
      height: 8px;
      width: 1px;
      background-color: #dfdfdf;
      margin: 0 8px;
    }
  }
}
</style>
